import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/ResponsiveDataTable';
import Select from 'react-select';
import { getOmsUsers, updateUserAccess, updateUserRole, updateUserStatus, updateUserWarehouseAccess, getWarehouses, deleteUser } from '../config/api';

const AccessManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [warehouseOptions, setWarehouseOptions] = useState([]);

  const accessOptions = [
    { value: 'marketing', label: 'Marketing', color: '#20c997' },
    { value: 'finance', label: 'Finance', color: '#6f42c1' },
    { value: 'ops', label: 'Operations', color: '#dc3545' },
    { value: 'delivery', label: 'Delivery', color: '#fd7e14' },
    { value: 'pricing', label: 'Pricing', color: '#3714fdff' },
    { value: 'crm', label: 'CRM', color: '#0dcaf0' },
    { value: 'hubManager', label: 'Hub Manager', color: '#0dcaf0' }
  ];

  const roleOptions = [
    { value: 'user', label: 'User', color: '#6c757d' },
    { value: 'manager', label: 'Manager', color: '#0dcaf0' },
    { value: 'admin', label: 'Admin', color: '#0d6efd' },
    { value: 'superAdmin', label: 'Super Admin', color: '#198754' }
  ];

  const roleChangeOptions = [
    { value: 'user', label: 'User' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Admin' }
  ];

  const loggedInUser = JSON.parse(localStorage.getItem('userData_30jan') || '{}');

  useEffect(() => {
    loadUsers();
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses();
      if (response && response.data) {
        // Transform to React-Select format
        const options = response.data.map(warehouse => ({
          value: warehouse.id,
          label: warehouse.name
        }));
        setWarehouseOptions(options);
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getOmsUsers() || {};
      console.log("data is ", data);
      setUsers(data.allUsers || []);
      console.log("user data is ", users);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccessChange = async (userId, accessType, isGranted, currentUser) => {

    try {
      setUpdating(true);
      await updateUserAccess(userId, accessType, isGranted);
      
      setUsers(users.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              access: isGranted 
                ? [...(user.access || []), accessType]
                : (user.access || []).filter(a => a !== accessType),
              access_changed_at: new Date().toISOString(),
              access_changed_by: JSON.parse(localStorage.getItem('userData_30jan') || '{}').id
            }
          : user
      ));
      
    } catch (error) {
      console.error('Error updating user access:', error);
      alert('Failed to update user access. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleRoleChange = async (userId, newRole, currentUser) => {
    if (!window.confirm(`Are you sure you want to change ${currentUser.name}'s role to ${newRole}?`)) {
      return;
    }

    try {
      setUpdating(true);
      await updateUserRole(userId, newRole);
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              role: newRole,
              role_changed_at: new Date().toISOString(),
              role_changed_by: JSON.parse(localStorage.getItem('userData_30jan') || '{}').id
            }
          : user
      ));
      
      alert(`Successfully updated ${currentUser.name}'s role to ${newRole}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (userId, newStatus, currentUser) => {
    const statusText = newStatus ? 'active' : 'inactive';
    // if (!window.confirm(`Are you sure you want to change ${currentUser.name}'s status to ${statusText}?`)) {
    //   return;
    // }

    try {
      setUpdating(true);
      await updateUserStatus(userId, newStatus);
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              active: newStatus,
              status_changed_at: new Date().toISOString(),
              status_changed_by: JSON.parse(localStorage.getItem('userData_30jan') || '{}').id
            }
          : user
      ));
      
      alert(`Successfully updated ${currentUser.name}'s status to ${statusText}`);
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleWarehouseAccessChange = async (userId, selectedOptions, currentUser) => {
    try {
      setUpdating(true);
      
      // Get current warehouse access
      const currentWarehouseAccess = currentUser.warehouse_access || [];
      const newWarehouseAccess = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
      
      // Find added and removed warehouses
      const addedWarehouses = newWarehouseAccess.filter(id => !currentWarehouseAccess.includes(id));
      const removedWarehouses = currentWarehouseAccess.filter(id => !newWarehouseAccess.includes(id));
      
      // Update each warehouse access
      for (const warehouseId of addedWarehouses) {
        await updateUserWarehouseAccess(userId, warehouseId, true);
      }
      
      for (const warehouseId of removedWarehouses) {
        await updateUserWarehouseAccess(userId, warehouseId, false);
      }
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              warehouse_access: newWarehouseAccess,
              access_changed_at: new Date().toISOString(),
              access_changed_by: JSON.parse(localStorage.getItem('userData_30jan') || '{}').id
            }
          : user
      ));
      
    } catch (error) {
      console.error('Error updating warehouse access:', error);
      alert('Failed to update warehouse access. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (userId, currentUser) => {
    if (!window.confirm(`⚠️ Are you sure you want to delete user "${currentUser.name}"?`)) {
      return;
    }

    try {
      setUpdating(true);
      const response = await deleteUser(userId);
      
      if (response.success) {
        // Remove user from local state
        setUsers(users.filter(user => user.id !== userId));
        alert(`Successfully deleted user: ${currentUser.name}`);
      } else {
        alert(response.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const getAccessBadge = (accessType) => {
    const accessConfig = accessOptions.find(a => a.value === accessType) || { label: accessType, color: '#6c757d' };
    return (
      <span 
        className="status-badge" 
        style={{ backgroundColor: accessConfig.color, color: 'white', margin: '2px', fontSize: '10px' }}
      >
        {accessConfig.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = roleOptions.find(r => r.value === role) || roleOptions[0];
    return (
      <span 
        className="status-badge" 
        style={{ backgroundColor: roleConfig.color, color: 'white' }}
      >
        {roleConfig.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const isActive = status === true || status === 'active' || status === 1;
    return (
      <span 
        className="status-badge" 
        style={{ 
          backgroundColor: isActive ? '#198754' : '#dc3545', 
          color: 'white' 
        }}
      >
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangedByName = (userId) => {
    if (!userId) return 'System';
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown';
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      width: '200px',
      cell: row => (
        <div>
          <div style={{ fontWeight: '600', color: '#212529' }}>{row.name}</div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>{row.email}</div>
        </div>
      ),
    },
    {
      name: 'Current Role',
      selector: row => row.role,
      sortable: true,
      cell: row => getRoleBadge(row.role),
      width: '165px',
    },
    {
      name: 'Change Role',
      cell: row => (
        <select
          value={row.role}
          onChange={(e) => handleRoleChange(row.id, e.target.value, row)}
          disabled={updating || row.id == loggedInUser.id}
          className="form-control"
          style={{ fontSize: '12px', padding: '4px 8px', width: '120px' }}
        >
          {roleChangeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: '150px',
    },
    {
      name: 'Current Status',
      selector: row => row.active,
      sortable: true,
      cell: row => getStatusBadge(row.active),
      width: '140px',
    },
    {
      name: 'Change Status',
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={row.active === true || row.active === 'active' || row.active === 1}
              onChange={(e) => handleStatusChange(row.id, e.target.checked, row)}
              disabled={updating || row.id == loggedInUser.id}
              style={{ width: 'auto' }}
            />
            <span style={{ fontWeight: '500', color: '#495057' }}>
              {(row.active === true || row.active === 'active' || row.active === 1) ? 'Active' : 'Inactive'}
            </span>
          </label>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: '150px',
    },
    {
      name: 'Current Access',
      selector: row => row.access,
      sortable: false,
      cell: row => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
          {(row.access && row.access.length > 0) ? 
            row.access.map(access => getAccessBadge(access)) :
            <span style={{ color: '#6c757d', fontSize: '12px' }}>No Access</span>
          }
        </div>
      ),
      width: '170px',
    },
    {
      name: 'Manage Access',
      cell: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {accessOptions.map(option => (
            <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px' }}>
              <input
                type="checkbox"
                checked={(row.access || []).includes(option.value)}
                onChange={(e) => handleAccessChange(row.id, option.value, e.target.checked, row)}
                disabled={updating}
                style={{ width: 'auto' }}
              />
              <span style={{ color: option.color, fontWeight: '500' }}>{option.label}</span>
            </label>
          ))}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: '170px',
    },
    {
      name: 'Warehouse Access',
      cell: row => {
        const selectedWarehouses = warehouseOptions.filter(warehouse => 
          (row.warehouse_access || []).includes(warehouse.value)
        );
        
        return (
          <div style={{ width: '100%', minWidth: '200px' }}>
            <Select
              value={selectedWarehouses}
              onChange={(selectedOptions) => handleWarehouseAccessChange(row.id, selectedOptions, row)}
              options={warehouseOptions}
              isMulti
              isDisabled={updating}
              placeholder="Select warehouses..."
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '32px',
                  fontSize: '12px',
                }),
                multiValue: (base) => ({
                  ...base,
                  fontSize: '11px',
                }),
                menu: (base) => ({
                  ...base,
                  fontSize: '12px',
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {selectedWarehouses.length === 0 && (
              <small style={{ color: '#6c757d', fontSize: '10px', display: 'block', marginTop: '2px' }}>
                All warehouses
              </small>
            )}
          </div>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      width: '250px',
    },
    {
      name: 'Actions',
      cell: row => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => handleDeleteUser(row.id, row)}
            disabled={updating || row.id == loggedInUser.id}
            className="btn btn-secondary"
            style={{ 
              fontSize: '12px', 
              padding: '6px 12px',
              backgroundColor: '#dc3545',
              borderColor: '#dc3545',
              color: 'white',
              opacity: (updating || row.id == loggedInUser.id) ? 0.5 : 1,
              cursor: (updating || row.id == loggedInUser.id) ? 'not-allowed' : 'pointer'
            }}
            title={row.id == loggedInUser.id ? 'Cannot delete your own account' : 'Delete user'}
          >
            🗑️ Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      width: '120px',
    },
    {
      name: 'Last Changed',
      selector: row => row.updatedAt,
      sortable: true,
      cell: row => (
        <div style={{ fontSize: '12px' }}>
          <div>{formatDateTime(row.updatedAt)}</div>
          {row.updatedAt && (
            <div style={{ color: '#6c757d' }}>
              by {getChangedByName(row.changed_by)}
            </div>
          )}
        </div>
      ),
      width: '180px',
    },
    {
      name: 'Created',
      selector: row => row.createdAt,
      sortable: true,
      format: row => row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : "-",
      width: '120px',
    },
  ];

  // Filter data based on search text
  const filteredItems = users.filter(
    item => {
      const searchText = filterText.toLowerCase();
      const statusText = (item.status === true || item.status === 'active' || item.status === 1) ? 'active' : 'inactive';
      return (
        item.name.toLowerCase().includes(searchText) ||
        item.email.toLowerCase().includes(searchText) ||
        item.role.toLowerCase().includes(searchText) ||
        statusText.includes(searchText) ||
        (item.access && item.access.some(a => a.toLowerCase().includes(searchText)))
      );
    }
  );

  const subHeaderComponent = useMemo(() => {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        width: '100%',
        padding: '8px 0'
      }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
          <input
            type="text"
            placeholder="Search users..."
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            style={{
              padding: '10px 14px',
              border: '2px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              width: '100%',
              transition: 'all 0.3s ease',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#0d6efd';
              e.target.style.boxShadow = '0 0 0 2px rgba(13, 110, 253, 0.25)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#ced4da';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>
    );
  }, [filterText]);

  const customStyles = {
    header: {
      style: {
        minHeight: '56px',
        paddingLeft: '0px',
        paddingRight: '0px',
      },
    },
    headRow: {
      style: {
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#dee2e6',
        backgroundColor: '#f8f9fa',
      },
    },
    headCells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        backgroundColor: '#f8f9fa',
        fontWeight: '600',
        fontSize: '14px',
        color: '#495057',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '16px',
      },
    },
    cells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        padding: '16px',
        fontSize: '14px',
      },
    },
    rows: {
      style: {
        '&:hover': {
          backgroundColor: '#f8f9fa',
        },
        borderBottomColor: '#dee2e6',
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #dee2e6',
        padding: '16px',
      },
    },
  };

  return (
    <div className="container">
      {/* Page Header */}
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title">Access Management</h1>
            <p className="page-subtitle">Manage user roles and permissions</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ 
              padding: '8px 16px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#856404'
            }}>
              <strong>⚠️ Super Admin Only</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table Card */}
      <div className="card">
        <DataTable
          columns={columns}
          data={filteredItems}
          progressPending={loading}
          pagination
          paginationResetDefaultPage={false}
          subHeader
          subHeaderComponent={subHeaderComponent}
          selectableRows={false}
          customStyles={customStyles}
          highlightOnHover
          responsive
          striped
          paginationPerPage={10}
          paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
          noDataComponent={
            <div style={{ 
              padding: '60px 40px', 
              textAlign: 'center',
              color: '#6c757d',
              fontSize: '16px'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>No users found</div>
              <div>Try adjusting your search criteria</div>
            </div>
          }
          defaultSortFieldId="name"
          defaultSortAsc={true}
        />
      </div>

      {/* Role Definitions Section */}
      <div className="card">
        <h3 className="section-title">Role & Access Definitions</h3>
        <div className="role-definitions">
          <div className="role-definition-item">
            <h4 style={{ color: '#198754', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
              🔧 Super Admin
            </h4>
            <ul style={{ margin: '0', paddingLeft: '20px', color: '#495057', lineHeight: '1.6' }}>
              <li>Full system access and control</li>
              <li>Manage all users, roles, and permissions</li>
              <li>Access to all modules and features</li>
              <li>View and manage all orders, products, and payments</li>
            </ul>
          </div>

          {/* <div className="role-definition-item">
            <h4 style={{ color: '#0d6efd', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
              👤 Admin
            </h4>
            <ul style={{ margin: '0', paddingLeft: '20px', color: '#495057', lineHeight: '1.6' }}>
              <li>Limited administrative access</li>
              <li>Cannot manage other admin users</li>
              <li>Access based on assigned permissions</li>
              <li>Can view and manage assigned modules</li>
            </ul>
          </div> */}

          {/* <div className="role-definition-item">
            <h4 style={{ color: '#6c757d', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
              👥 User
            </h4>
            <ul style={{ margin: '0', paddingLeft: '20px', color: '#495057', lineHeight: '1.6' }}>
              <li>Basic access to assigned modules</li>
              <li>Cannot manage other users</li>
              <li>View and edit permissions based on access level</li>
            </ul>
          </div> */}
        </div>

        <div style={{ marginTop: '32px' }}>
          <h4 style={{ color: '#212529', marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>
            📋 Access Permissions
          </h4>
          <div className="access-definitions">
            <div className="access-definition-grid" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
              gap: '20px' 
            }}>
              <div className="access-definition-item" style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h5 style={{ color: '#20c997', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  📈 Marketing Access
                </h5>
                <ul style={{ margin: '0', paddingLeft: '16px', fontSize: '13px', color: '#495057' }}>
                  <li>View and manage products</li>
                  <li>Create and edit product listings</li>
                </ul>
              </div>

              <div className="access-definition-item" style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h5 style={{ color: '#6f42c1', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  💰 Finance Access
                </h5>
                <ul style={{ margin: '0', paddingLeft: '16px', fontSize: '13px', color: '#495057' }}>
                  <li>View and manage payments</li>
                  <li>Approve wallet payments</li>
                </ul>
              </div>

              {/* <div className="access-definition-item" style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h5 style={{ color: '#dc3545', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  ⚙️ Operations Access
                </h5>
                <ul style={{ margin: '0', paddingLeft: '16px', fontSize: '13px', color: '#495057' }}>
                  <li>Manage order processing</li>
                  <li>Update order statuses</li>
                  <li>Handle order fulfillment</li>
                  <li>Manage inventory</li>
                </ul>
              </div> */}

              {/* <div className="access-definition-item" style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h5 style={{ color: '#fd7e14', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  🚚 Delivery Access
                </h5>
                <ul style={{ margin: '0', paddingLeft: '16px', fontSize: '13px', color: '#495057' }}>
                  <li>Manage delivery schedules</li>
                  <li>Update delivery status</li>
                  <li>Handle shipping logistics</li>
                  <li>Track deliveries</li>
                </ul>
              </div> */}

              <div className="access-definition-item" style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h5 style={{ color: '#3714fdff', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  💲 Pricing Access
                </h5>
                <ul style={{ margin: '0', paddingLeft: '16px', fontSize: '13px', color: '#495057' }}>
                  <li>View and edit product pricing</li>
                  <li>Manage price slabs</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessManagement;
